import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { graphql } from 'babel-plugin-relay/macro';
import { Button, ButtonGroup, ButtonGroupProps, ButtonProps } from 'embeds/components';
import { filter, find } from 'lodash';

import { ButtonChatMessageSubmit_EmbedPatient$key } from './__generated__/ButtonChatMessageSubmit_EmbedPatient.graphql';
import PopperPatientContactSelect, { PopperPatientContactSelectProps } from './PopperPatientContactSelect';

const messages = defineMessages({
  label: {
    id: 'deprecated.fragments.ButtonChatMessageSubmit.label',
    defaultMessage: 'Send',
  },
});

export interface ButtonChatMessageSubmitProps extends Omit<ButtonGroupProps, 'classes'> {
  fragmentRef: ButtonChatMessageSubmit_EmbedPatient$key;
  selectedContacts?: PopperPatientContactSelectProps['selectedContacts'];
  onContactsSelected: PopperPatientContactSelectProps['onContactsSelected'];
  visibility: 'internal' | 'public';
  type?: ButtonProps['type'];
  onClick?: ButtonProps['onClick'];
}

const ButtonChatMessageSubmit: React.FC<ButtonChatMessageSubmitProps> = ({
  fragmentRef,
  selectedContacts,
  onContactsSelected,
  visibility,
  disabled: disabledProp,
  type,
  onClick,
  ...rest
}) => {
  const patient = useFragment<ButtonChatMessageSubmit_EmbedPatient$key>(
    graphql`
      fragment ButtonChatMessageSubmit_EmbedPatient on Patient {
        doNotContact
        contact {
          active
          type
        }
        ...PopperPatientContactSelect_EmbedPatient
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();

  const disabled = useMemo(() => {
    if (disabledProp || !patient || patient.doNotContact) {
      return true;
    }
    if (visibility === 'internal') {
      return false;
    }
    const items = filter(patient.contact, 'active');
    return !(find(items, { type: 'sms' }) || find(items, { type: 'email' }) || find(items, { type: 'whatsapp' }));
  }, [disabledProp, visibility, patient]);

  const sendDisabled = useMemo(() => {
    if (selectedContacts && selectedContacts.length === 0) {
      return true;
    }
    return disabled;
  }, [disabled, selectedContacts]);

  return (
    <>
      <ButtonGroup disablePadding {...rest}>
        <Button
          data-lh-id="ButtonChatMessageSubmit-btn-submit"
          type={type}
          aria-label="Send Message"
          disabled={sendDisabled}
          onClick={onClick}
        >
          {formatMessage(messages.label)}
        </Button>
        {visibility === 'public' && (
          <Button
            data-lh-id="ButtonChatMessageSubmit-btn-select-contact"
            aria-controls={anchorEl ? 'split-button-menu' : undefined}
            aria-expanded={anchorEl ? 'true' : undefined}
            aria-label="select contact"
            aria-haspopup="menu"
            disabled={disabled}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {anchorEl ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
          </Button>
        )}
      </ButtonGroup>
      <PopperPatientContactSelect
        fragmentRef={patient}
        placement="top-end"
        PaperProps={{ square: true }}
        gutterBottom
        anchorEl={anchorEl}
        onClickAway={() => setAnchorEl(undefined)}
        selectedContacts={selectedContacts}
        onContactsSelected={onContactsSelected}
      />
    </>
  );
};

export default ButtonChatMessageSubmit;
